/**
 * Task V2 Repository - GraphQL Queries
 *
 * Query definitions for Task primitive.
 * 7 queries matching backend task_graphql.rs.
 *
 * @see graphql_gateway/src/task_api/task_graphql.rs - Backend resolvers
 */

import { gql } from '@apollo/client';
import {
  TASK_FIELDS_FRAGMENT,
  TASK_CONNECTION_FRAGMENT,
  TASK_STATISTICS_FRAGMENT,
} from './fragments';

/** Get single task by ID */
export const GET_TASK = gql`
  query GetTask($tenantId: String!, $taskId: String!) {
    taskApiGetTask(tenantId: $tenantId, taskId: $taskId) {
      ...TaskFields
    }
  }
  ${TASK_FIELDS_FRAGMENT}
`;

/** List tasks with pagination */
export const LIST_TASKS = gql`
  query ListTasks($tenantId: String!, $limit: Int, $cursor: String) {
    taskApiListTasks(tenantId: $tenantId, limit: $limit, cursor: $cursor) {
      ...TaskConnectionFields
    }
  }
  ${TASK_CONNECTION_FRAGMENT}
`;

/** Get tasks by workflow instance */
export const GET_TASKS_BY_WORKFLOW_INSTANCE = gql`
  query GetTasksByWorkflowInstance(
    $tenantId: String!
    $workflowInstanceId: String!
  ) {
    taskApiGetTasksByWorkflowInstance(
      tenantId: $tenantId
      workflowInstanceId: $workflowInstanceId
    ) {
      ...TaskFields
    }
  }
  ${TASK_FIELDS_FRAGMENT}
`;

/** Get tasks assigned to actor */
export const GET_TASKS_BY_ACTOR = gql`
  query GetTasksByActor($tenantId: String!, $actorId: String!) {
    taskApiGetTasksByActor(tenantId: $tenantId, actorId: $actorId) {
      ...TaskFields
    }
  }
  ${TASK_FIELDS_FRAGMENT}
`;

/** Get tasks by status */
export const GET_TASKS_BY_STATUS = gql`
  query GetTasksByStatus($tenantId: String!, $status: TaskStatus!) {
    taskApiGetTasksByStatus(tenantId: $tenantId, status: $status) {
      ...TaskFields
    }
  }
  ${TASK_FIELDS_FRAGMENT}
`;

/** Get overdue tasks */
export const GET_OVERDUE_TASKS = gql`
  query GetOverdueTasks($tenantId: String!) {
    taskApiGetOverdueTasks(tenantId: $tenantId) {
      ...TaskFields
    }
  }
  ${TASK_FIELDS_FRAGMENT}
`;

/** Get task statistics */
export const GET_TASK_STATISTICS = gql`
  query GetTaskStatistics($tenantId: String!) {
    taskApiGetTaskStatistics(tenantId: $tenantId) {
      ...TaskStatisticsFields
    }
  }
  ${TASK_STATISTICS_FRAGMENT}
`;

/**
 * Get complete task history (Pure Append-Only Architecture)
 *
 * Returns ALL versions of a task sorted chronologically (V1 → Vn).
 * Uses DynamoDB GSI5 for <10ms query performance.
 * Includes complete reassignment trail.
 *
 * @example
 * ```typescript
 * const { data } = await client.query<Record<string, any>>({
 *   query: GET_TASK_HISTORY,
 *   variables: {
 *     tenantId: "healthcare-tenant",
 *     workflowInstanceId: "wf_inst_123",
 *     taskId: "task_abc"
 *   }
 * });
 * // data?.getTaskHistory = [
 * //   { version: 1, status: "Created", assignedActorId: null, parentId: null },
 * //   { version: 2, status: "Assigned", assignedActorId: "actor-001", parentId: "task_abc" },
 * //   { version: 4, status: "Assigned", assignedActorId: "actor-002", parentId: "task_def",
 * //     reassignmentHistory: [{from: "actor-001", to: "actor-002"}] }
 * // ]
 * // All share same rootTaskId = "task_abc"
 * ```
 */
export const GET_TASK_HISTORY = gql`
  query GetTaskHistory(
    $tenantId: String!
    $workflowInstanceId: String!
    $taskId: String!
  ) {
    getTaskHistory(
      tenantId: $tenantId
      workflowInstanceId: $workflowInstanceId
      taskId: $taskId
    ) {
      ...TaskFields
    }
  }
  ${TASK_FIELDS_FRAGMENT}
`;
